import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { clientPayments } from '../../utils/clientPayments';
import { formatMoney, pnlClass } from './adminMoney';
import { jtncUnavailableHint, jtncUnavailableTitle } from './jtncLedgerText';

const useClientPaymentRows = (email) => {
  const [state, setState] = useState({ rows: null, error: null });
  useEffect(() => {
    const raw = String(email || '').trim();
    if (!raw || !db) {
      setState({ rows: raw ? null : [], error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ rows: null, error: null });
    const emails = [...new Set([raw, raw.toLowerCase()])];
    const col = collection(db, 'tbl_stripe_payment_history');
    Promise.all([
      getDocs(query(col, where('email', 'in', emails))),
      getDocs(query(col, where('customerEmail', 'in', emails))),
    ])
      .then((snaps) => {
        const byId = new Map();
        snaps.forEach((s) => s.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() })));
        if (!cancelled) setState({ rows: [...byId.values()], error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ rows: null, error: err?.message || 'Failed to load payments' });
      });
    return () => { cancelled = true; };
  }, [email]);
  return state;
};

/** "Paid to JTNC" and "Net to client" — what the client paid JTNC against their JTNC Trade P&L. */
const AdminClientPaymentsCards = ({ email, jtncPnl, jtncError }) => {
  const { rows, error } = useClientPaymentRows(email);
  const paid = useMemo(() => (rows ? clientPayments(rows) : null), [rows]);

  if (!email) {
    return <div className="mt-2 text-xs text-gray-500">No email on this client — payments cannot be matched.</div>;
  }
  if (error) {
    return <div className="mt-2 text-xs text-red-300/90">Payments unavailable: {error}</div>;
  }

  const parts = paid
    ? [
        paid.deposit && `deposit ${formatMoney(paid.deposit, { signed: false })}`,
        paid.monthly && `monthly ${formatMoney(paid.monthly, { signed: false })}`,
        paid.other && `annual / one-time ${formatMoney(paid.other, { signed: false })}`,
      ].filter(Boolean)
    : [];
  const paymentLines = paid?.payments.map((p) => `${p.date}  ${formatMoney(p.amount, { signed: false })}  ${p.description}`) || [];
  const notes = [
    'From Kajabi / Stripe payment records matched by the client\'s email. Test, trial, $1 affiliate and $0 rows are left out.',
    paid?.echo ? `Echo payments (${formatMoney(paid.echo, { signed: false })}) are a separate product and are not included.` : null,
    paid?.renewalsMissing ? 'Monthly renewals billed through Kajabi (and the Monthly Maintenance Fee) are recorded only for the first charge, so later months are missing.' : null,
  ].filter(Boolean);
  const net = paid && jtncPnl != null ? Math.round((jtncPnl - paid.total) * 100) / 100 : null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 bg-gray-800/60 rounded-lg px-3 py-1.5 text-xs">
      <span title={[...notes, '', ...paymentLines].join('\n')}>
        <span className="text-gray-500">Paid to JTNC </span>
        <span className="font-semibold tabular-nums text-gray-200">
          {paid ? formatMoney(paid.total, { signed: false }) : '…'}
        </span>
        {paid && (
          <span className="text-[10px] text-gray-500"> ({parts.join(' · ') || 'no payments on record'})</span>
        )}
      </span>
      <span
        title={jtncError
          ? jtncUnavailableTitle(jtncError)
          : 'JTNC QFAA Trade P&L minus everything the client paid JTNC (deposit, monthly and annual fees).'}
      >
        <span className="text-gray-500">Net to client </span>
        <span className={`font-semibold tabular-nums ${net != null ? pnlClass(net) : 'text-gray-200'}`}>
          {net != null ? formatMoney(net) : (jtncError ? '—' : '…')}
        </span>
        {jtncError && <span className="text-[10px] text-amber-400/80"> {jtncUnavailableHint(jtncError)}</span>}
      </span>
      {paid?.renewalsMissing && (
        <span className="text-[10px] text-amber-400/80">Later monthly renewals not on record</span>
      )}
    </div>
  );
};

export default AdminClientPaymentsCards;
